import { formatDistanceToNow } from "date-fns";
import { Bell, Check, ExternalLink, RefreshCw, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotificationDropdown } from "@/features/notifications/api/notifications-api";
import type { Notification } from "../types/notifications";

const ENTITY_ICONS: Record<string, string> = {
  milestones: "🧱",
  payable_invoices: "💰",
  client_invoices: "🧾",
  change_orders: "✍️",
  material_decisions: "🎨",
  tasks: "📋",
  punchlist_items: "🔧",
  budget_categories: "📊",
  daily_logs: "📝",
};

function getEntityIcon(entityType?: string | null) {
  if (!entityType) return "🔔";
  return ENTITY_ICONS[entityType] || "🔔";
}

function formatRelativeTime(dateString: string) {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return "";
  }
}

type NotificationDropdownProps = {
  unreadCount: number;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  viewAllHref: string;
  isPortal?: boolean;
};

const NotificationDropdown = ({
  unreadCount,
  onMarkRead,
  onMarkAllRead,
  viewAllHref,
  isPortal = false,
}: NotificationDropdownProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: notifications = [], isLoading } = useNotificationDropdown(open, isPortal);

  const handleRowClick = (notification: Notification) => {
    if (!notification.isRead) {
      onMarkRead(notification.id);
    }
    if (notification.link) {
      window.location.assign(notification.link);
    }
    setOpen(false);
  };

  const handleCreateInvoice = (notification: Notification) => {
    if (!notification.isRead) {
      onMarkRead(notification.id);
    }
    const ctx = notification.context || {};
    const projectId = notification.projectId;
    const amount = ctx.amount || "";
    const changeOrderTitle = ctx.changeOrderTitle || "";
    if (projectId) {
      const params = new URLSearchParams();
      params.set("tab", "client-invoices");
      if (amount) params.set("prefillAmount", String(amount));
      if (changeOrderTitle) params.set("prefillNote", `For Change Order: ${changeOrderTitle}`);
      navigate(`/projects/${projectId}?${params.toString()}`);
    }
    setOpen(false);
  };

  const displayCount = unreadCount > 99 ? "99+" : unreadCount;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-(--color-bg-muted) transition-colors text-(--color-text-secondary)"
          aria-label="Notifications"
        >
          <Bell className="w-[18px] h-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-(--color-danger-500) text-white text-[10px] font-medium flex items-center justify-center">
              {displayCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 md:w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-(--color-border-default)">
          <span className="text-sm font-semibold text-(--color-text-primary)">
            Notifications
          </span>
          {unreadCount > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkAllRead();
              }}
              className="text-xs text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1 transition-colors"
            >
              <Check className="w-3 h-3" />
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[320px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-4 h-4 animate-spin text-(--color-text-tertiary)" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-(--color-text-tertiary)">
              No notifications yet
            </div>
          ) : (
            notifications.slice(0, 8).map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                onSelect={() => handleRowClick(notification)}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer focus:bg-(--color-bg-muted) ${
                  !notification.isRead ? "bg-(--color-bg-muted)" : ""
                }`}
              >
                <span className="mt-0.5 text-base leading-none shrink-0">
                  {getEntityIcon(notification.entityType)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {!notification.isRead && (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-(--color-warning-500) shrink-0" />
                    )}
                    <p
                      className={`text-sm leading-snug ${
                        notification.isRead
                          ? "text-(--color-text-secondary)"
                          : "text-(--color-text-primary) font-medium"
                      }`}
                    >
                      {notification.message}
                    </p>
                  </div>
                  <p className="text-xs text-(--color-text-tertiary) mt-1">
                    {formatRelativeTime(notification.createdAt)}
                  </p>
                  {notification.type === "change_order_invoice_suggested" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateInvoice(notification);
                      }}
                      className="mt-2 inline-flex items-center gap-1 rounded-md bg-(--color-warning-500) px-2 py-1 text-xs font-medium text-white hover:bg-(--color-warning-600)"
                    >
                      <Plus className="w-3 h-3" />
                      Create Invoice
                    </button>
                  )}
                </div>
                {notification.link && notification.type !== "change_order_invoice_suggested" && (
                  <ExternalLink className="w-3 h-3 text-(--color-text-tertiary) shrink-0 mt-1" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </div>

        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-4 py-2">
              <Link
                to={viewAllHref}
                className="block text-center text-xs text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
                onClick={() => setOpen(false)}
              >
                View all notifications →
              </Link>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDropdown;
